import { inject, Injectable } from '@angular/core';
import { Room, RoomsStore } from '../../state/rooms.repository';
import { generateRoomName } from '../../features/rooms/room-dialog/room-name-generator';

/**
 * Sentinel id prefix for fixture rooms: identifies them for `clear()` and can
 * never collide with real rooms (those ids are server-minted UUIDs). Note that
 * a server `rooms-snapshot` (e.g. on WS reconnect) replaces the whole store
 * and wipes fixture rooms anyway — they are throwaway dev data.
 */
const DUMMY_ROOM_ID_PREFIX = '__dummy_room_';

const randomInt = (min: number, max: number): number =>
  min + Math.floor(Math.random() * (max - min + 1));

/** ~50-char name to exercise truncation (plate ellipsis, tooltips). */
const longRoomName = (): string => {
  let name = generateRoomName();
  while (name.length < 50) name += ` ${generateRoomName()}`;
  return name.slice(0, 50).trimEnd();
};

/**
 * Seeds the rooms store with dummy rooms so room-browsing UIs (room login
 * list, room plan) can be exercised without a populated server. Driven from
 * the dev console (`window.rooms.*`).
 */
@Injectable({ providedIn: 'root' })
export class RoomsFixtureService {
  private readonly _rooms = inject(RoomsStore);

  /**
   * Adds `count` dummy rooms with varied presence: roughly half hold 1–8
   * members (all fit around a room-plan table) and half hold more (exercises
   * the overflow badge). Privacy/password flags are rolled per room.
   */
  addRoomsFixture(count = 15): void {
    for (let i = 0; i < count; i++) {
      const cozy = Math.random() < 0.5;
      const room: Room = {
        roomId: `${DUMMY_ROOM_ID_PREFIX}${i}`,
        // Every fifth room gets an overlong name to keep truncation honest.
        name: i % 5 === 4 ? longRoomName() : generateRoomName(),
        isPrivate: Math.random() < 0.4,
        hasPassword: Math.random() < 0.4,
        isHidden: false,
        memberCount: cozy ? randomInt(1, 8) : randomInt(9, 60),
      };
      this._rooms.add(room);
    }
  }

  /** Removes fixture rooms only — rooms synced from the server are kept. */
  clear(): void {
    this._rooms.rooms
      .filter((room) => room.roomId.startsWith(DUMMY_ROOM_ID_PREFIX))
      .forEach((room) => this._rooms.remove(room.roomId));
  }
}
